'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { Project, Workspace } from '@frameio/graphql-client/generated';
import { ErrorBoundary } from '@frameio/shared/components/ErrorBoundary/ErrorBoundary';
import {
  useActiveAccountId,
  useActiveProjectId,
} from '@frameio/shared/contexts/ActiveEntitiesContext';
import { useProjectSortBy } from '@frameio/shared/hooks';
import { DEFAULT_PROJECT_FILTERS } from '@frameio/shared/resources/constants';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { mapSortByToProjectOrderInputs } from '@frameio/shared/utils';
import { Box, theme, usePrevious, useSheetContext } from '@frameio/vapor';
import { RECENT_PROJECTS_THRESHOLD } from '@frameio/workspaces';
import {
  useRecentProjects,
  useRecentProjectsCount,
  useRecentProjectsLoading,
} from '@frameio/workspaces/shared';
import TEXT from './generated/TEXT/ProjectSwitcherSheet';
import {
  useGroupProjectsByWorkspaces,
  usePaginatedGetProjectsForWorkspacesList,
} from './hooks';
import { NoResultsMessage } from './NoResultsMessage';
import {
  ProjectSwitcherProjectList,
  RECENT_LOADING_ID,
  RECENT_WORKSPACE_ID,
} from './ProjectSwitcherProjectList';
import { ProjectSwitcherProjectSettings } from './ProjectSwitcherProjectSettings';
import { ProjectSwitcherSheetHeader } from './ProjectSwitcherSheetHeader';
import { ProjectSwitcherSheetSearch } from './ProjectSwitcherSheetSearch';
import { ProjectSwitcherSheetSort } from './ProjectSwitcherSheetSort';

export const ProjectSwitcherSheet = memo(() => {
  const accountId = useActiveAccountId();
  const activeProjectId = useActiveProjectId();
  const ref = useRef(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [sortBy] = useProjectSortBy();
  const lastSortBy = usePrevious(sortBy);
  const { isShowing: isSheetShowing } = useSheetContext();
  const listRef = useRef<HTMLUListElement>(null);
  const track = useTrackUnsafe();

  const {
    data: projectsForWorkspacesListData,
    loading: projectsLoading,
    error,
    fetchNextPage,
    refetch,
  } = usePaginatedGetProjectsForWorkspacesList({
    accountId,
    filters: DEFAULT_PROJECT_FILTERS,
    sortBy: mapSortByToProjectOrderInputs(sortBy),
  });

  const refetchProjectsWithQuery = useCallback(
    (value: string) => {
      refetch({
        accountId,
        query: value,
        filters: DEFAULT_PROJECT_FILTERS,
        sortBy: mapSortByToProjectOrderInputs(sortBy),
      });
    },
    [accountId, refetch, sortBy]
  );

  useEffect(() => {
    if (lastSortBy !== sortBy) {
      refetch({
        accountId,
        query,
        filters: DEFAULT_PROJECT_FILTERS,
        sortBy: mapSortByToProjectOrderInputs(sortBy),
      });
    }
  }, [sortBy, accountId, refetch, query, lastSortBy]);

  useEffect(() => {
    if (initialLoading) {
      setInitialLoading(projectsLoading);
    }
  }, [initialLoading, projectsLoading]);

  const groupProjectsByWorkspaces = useGroupProjectsByWorkspaces();
  const recentProjects = useRecentProjects();
  const recentProjectsLoading = useRecentProjectsLoading();
  const recentProjectsCount = useRecentProjectsCount();

  const recentProjectsGroup = useMemo(() => {
    if (
      (projectsForWorkspacesListData?.account?.projects?.nodes?.length ?? 0) <
      RECENT_PROJECTS_THRESHOLD
    ) {
      return null;
    }
    return getRecentProjectsGroup({
      recentProjects,
      count: recentProjectsCount,
      loading: recentProjectsLoading,
    }) as Workspace | null;
  }, [
    recentProjects,
    recentProjectsCount,
    recentProjectsLoading,
    projectsForWorkspacesListData,
  ]);

  const hasQuery = Boolean(query);
  const [groupedProjects, flattenedProjects] = useMemo(() => {
    const filteredGroup = groupProjectsByWorkspaces({
      data: projectsForWorkspacesListData,
    });

    if (recentProjectsGroup && !hasQuery) {
      filteredGroup.unshift(recentProjectsGroup);
    }

    const flatListOfProjects = filteredGroup.flatMap(
      (workspace) => workspace?.projects?.nodes
    );

    return [filteredGroup, flatListOfProjects];
  }, [
    groupProjectsByWorkspaces,
    projectsForWorkspacesListData,
    hasQuery,
    recentProjectsGroup,
  ]);

  const selectedProject = flattenedProjects[activeIndex];

  /**
   * Handle keyboard navigation when filtering projects.
   */

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // eslint-disable-next-line default-case
      switch (e.key) {
        case 'Enter':
          if (selectedProject) {
            document.getElementById(`${selectedProject.id}`)?.click();
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          listRef.current?.focus(); // Defer control to List
          break;
        case 'ArrowUp':
          e.preventDefault();
          listRef.current?.focus(); // Defer control to List
          break;
      }
    },
    [selectedProject]
  );

  /**
   * When the query changes, always reset our active index
   * to the first index.
   */

  const onQueryChange = useCallback((value: string) => {
    setActiveIndex(0);
    setQuery(value);
  }, []);

  /**
   * Upon blur, we hide our keyboard activated state.
   */
  const resetActiveIndex = useCallback(() => {
    setActiveIndex(-1);
  }, []);

  // Reset the query when the sheet is hidden
  useEffect(() => {
    if (!isSheetShowing) {
      setQuery('');
      refetch({
        accountId,
        query: '',
        filters: DEFAULT_PROJECT_FILTERS,
        sortBy: mapSortByToProjectOrderInputs(sortBy),
      });
    }
  }, [isSheetShowing, refetch, accountId, sortBy]);

  useEffect(() => {
    if (isSheetShowing) {
      track('project-switcher-panel-opened');
    }
  }, [isSheetShowing, track]);

  const hasNoResults =
    query && groupedProjects?.length === 0 && !initialLoading;

  return (
    <ProjectSwitcherProjectSettings>
      <Layout ref={ref}>
        <ProjectSwitcherSheetHeader />
        <Box paddingX={16}>
          <SheetSearchContainer>
            <ProjectSwitcherSheetSearch
              aria-activedescendant={selectedProject?.id}
              debounce={150}
              onBlur={resetActiveIndex}
              onKeyDown={onKeyDown}
              onQueryChange={onQueryChange}
              onRequestSearch={refetchProjectsWithQuery}
            />
            <Box paddingLeft={8}>
              <ProjectSwitcherSheetSort />
            </Box>
          </SheetSearchContainer>
        </Box>
        <ErrorBoundary>
          {hasNoResults ? (
            <NoResultsMessage query={query} />
          ) : (
            <ProjectSwitcherProjectList
              activeProjectId={activeProjectId}
              fetchError={error}
              fetchNextPage={fetchNextPage}
              groupedProjects={groupedProjects}
              initialLoading={initialLoading}
              listRef={listRef}
              loadingMore={projectsLoading}
              query={query}
            />
          )}
        </ErrorBoundary>
      </Layout>
    </ProjectSwitcherProjectSettings>
  );
});

TEXT.DEFINE({
  /** Header for the Recent Projects section in the Project Switcher */
  recentProjects: 'Recent Projects',
});

const Layout = styled.div`
  display: grid;
  grid-template-rows: auto auto 1fr;
  height: 100%;
`;

const SheetSearchContainer = styled.div`
  align-items: center;
  display: flex;
  height: ${theme.spaces[64]};
  margin-top: ${theme.spaces[2]};
`;

const getRecentProjectsGroup = ({
  count,
  loading,
  recentProjects,
}: {
  count: number;
  loading: boolean;
  recentProjects: ReturnType<typeof useRecentProjects>;
}) => {
  if (!recentProjects && loading && count) {
    return {
      projects: {
        pageInfo: {
          hasNextPage: false,
          endCursor: null,
        },
        totalCount: count,
        nodes: [...Array(count)].map(
          (_, index) =>
            ({
              id: `${RECENT_LOADING_ID}-${index}`,
            }) as Project
        ),
      },
      archivedStorage: 0,
      storage: 0,
      id: RECENT_LOADING_ID,
      name: TEXT.recentProjects,
    };
  }
  if (recentProjects && count) {
    return {
      projects: {
        nodes: recentProjects.map(
          (project) =>
            ({
              ...project,
              workspaceId: project?.workspace?.id ?? RECENT_WORKSPACE_ID,
            }) as Project
        ),
        pageInfo: {
          hasNextPage: false,
          endCursor: null,
        },
        totalCount: count,
      },
      archivedStorage: 0,
      storage: 0,
      id: RECENT_WORKSPACE_ID,
      name: TEXT.recentProjects,
    };
  }
  return null;
};
